import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getClients,
  addNewClient,
  updateClient,
  deleteClient,
  getClientDetails,
} from "./thunk";
import {
  ApiResponse,
  PaginatedPayload,
  PaginationMeta,
} from "../../types/ApiResponse";
import { ClientDetailsPayload, ClientsPayloadData } from "types/client.api";
import { deleteQuote } from "slices/thunks";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface ClientStatus {
  fetch: RequestStatus;
  fetchById: RequestStatus;
  add: RequestStatus;
  edit: RequestStatus;
  delete: RequestStatus;
}

interface ClientError {
  fetch: string | null;
  fetchById: string | null;
  add: string | null;
  edit: string | null;
  delete: string | null;
}

export interface ClientState {
  clients: ClientsPayloadData[];
  isLoading: boolean;
  ids: number[];
  clientDetails: ClientDetailsPayload | null;
  pagination: PaginationMeta;
  status: ClientStatus;
  errors: ClientError;
}

export const initialState: ClientState = {
  clients: [],
  ids: [],
  clientDetails: null,
  isLoading: false,

  pagination: {
    current_page: 0,
    per_page: 0,
    total: 0,
    last_page: 0,
    path: "",
    from: 0,
    to: 0,
    links: [],
  },

  status: {
    fetch: "idle",
    fetchById: "idle",
    add: "idle",
    edit: "idle",
    delete: "idle",
  },

  errors: {
    fetch: null,
    fetchById: null,
    add: null,
    edit: null,
    delete: null,
  },
};

const clientSlice = createSlice({
  name: "client",
  initialState,
  reducers: {
    resetClientStatus: (state) => {
      state.status = {
        fetch: "idle",
        fetchById: "idle",
        add: "idle",
        edit: "idle",
        delete: "idle",
      };
      state.errors = {
        fetch: null,
        fetchById: null,
        add: null,
        edit: null,
        delete: null,
      };
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    // getClients cases
    builder.addCase(getClients.pending, (state) => {
      state.status.fetch = "loading";
      state.isLoading = true;
      state.errors.fetch = null;
    });
    builder.addCase(
      getClients.fulfilled,
      (
        state,
        action: PayloadAction<ApiResponse<PaginatedPayload<ClientsPayloadData>>>
      ) => {
        const responsePayload = action.payload?.payload;
        state.status.fetch = "succeeded";
        state.isLoading = false;
        state.clients = responsePayload?.data || [];
        state.ids = (responsePayload?.data || []).map((quote) => quote.id);
        state.pagination = responsePayload?.meta;
      }
    );
    builder.addCase(getClients.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
      state.isLoading = false;
    });

    builder.addCase(getClientDetails.pending, (state) => {
      state.status.fetchById = "loading";
      state.errors.fetchById = null;
    });
    builder.addCase(
      getClientDetails.fulfilled,
      (state, action: PayloadAction<ApiResponse<ClientDetailsPayload>>) => {
        state.status.fetchById = "succeeded";
        state.clientDetails = action.payload.payload;
      }
    );
    builder.addCase(getClientDetails.rejected, (state, action) => {
      state.status.fetchById = "failed";
      state.errors.fetchById = action.payload || null;
    });

    builder.addCase(addNewClient.pending, (state) => {
      state.status.add = "loading";
      state.isLoading = true;
      state.errors.add = null;
    });
    builder.addCase(
      addNewClient.fulfilled,
      (state, action: PayloadAction<ApiResponse<ClientsPayloadData>>) => {
        state.status.add = "succeeded";
        state.isLoading = false;
        const newClient = action.payload.payload;
        state.clients.unshift(newClient);
        state.ids.unshift(newClient.id);
        if (state.clients.length > state.pagination.per_page) {
          state.pagination.to--;
          state.clients.pop();
        }
        state.pagination.to++;
        state.pagination.total++;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page
        );
      }
    );
    builder.addCase(addNewClient.rejected, (state, action) => {
      state.status.add = "failed";
      state.isLoading = false;
      state.errors.add = action.payload || null;
    });

    builder.addCase(updateClient.pending, (state) => {
      state.status.edit = "loading";
      state.errors.edit = null;
    });
    builder.addCase(
      updateClient.fulfilled,
      (state, action: PayloadAction<ApiResponse<ClientsPayloadData>>) => {
        state.status.edit = "succeeded";

        const updatedClient = action.payload.payload;

        const index = state.clients.findIndex(
          (client) => client.id === updatedClient.id
        );

        state.clientDetails = updatedClient;

        if (index !== -1) {
          state.clients[index] = updatedClient;
        }
      }
    );
    builder.addCase(updateClient.rejected, (state, action) => {
      state.status.edit = "failed";
      state.errors.edit = action.payload || null;
    });

    builder.addCase(deleteClient.pending, (state) => {
      state.status.delete = "loading";
      state.errors.delete = null;
    });
    builder.addCase(
      deleteClient.fulfilled,
      (state, action: PayloadAction<ApiResponse<number>>) => {
        state.status.delete = "succeeded";
        const deletedclientId = action.payload.payload;
        state.clients = state.clients.filter(
          (client) => client.id !== deletedclientId
        );
        state.ids = state.ids.filter((id) => id !== deletedclientId);
        state.pagination.to--;
        state.pagination.total--;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page
        );
      }
    );
    builder.addCase(deleteClient.rejected, (state, action) => {
      state.status.delete = "failed";
      state.errors.delete = action.payload || null;
    });
    builder.addCase(
      deleteQuote.fulfilled,
      (state, action: PayloadAction<ApiResponse<number>>) => {
        const deletedQuoteId = action.payload.payload;
        console.log("deletedQuoteId", deletedQuoteId);
        if (state.clientDetails) {
          state.clientDetails.recent_activities =
            state.clientDetails.recent_activities.filter(
              (activity) => activity.id !== deletedQuoteId
            );
        }
      }
    );
  },
});

export default clientSlice.reducer;

export const { resetClientStatus } = clientSlice.actions;

export const selectClientsState = (state: any) =>
  state.client as typeof initialState;
